


import React from "react";
import { useTranslation } from "react-i18next";
import { Country, State } from "country-state-city";
import { truncateCommaSeparatedString } from "../../components/common/utlity/utility";
import numberToWord from "../../components/common/utlity/invoiceword";
import { AWS_URL } from "../../services/http.service";
import { getCurrencySymbol } from "../../components/common/utlity/options";
import { useSelector } from "react-redux";

const formatCurrency = (val, currency) => {
  let num = 0;
  if (typeof val === "number") {
    num = val;
  } else if (typeof val === "string") {
    num = parseFloat(val);
    if (isNaN(num)) num = 0;
  }
  return `${getCurrencySymbol(currency)} ${num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (str) => {
  if (!str) return "-";
  const date = new Date(str);
  if (isNaN(date)) return str;
  return date.toLocaleDateString();
};

const ModernInvoice2 = ({
  item,
  containerRef,
  businessName,
  phone,
  darkMode = false,
  bussiness,
  selectedColor = "bg-blue-500",
}) => {
  const { t } = useTranslation();
  const user = useSelector((state) => state.user.user);

  if (!item) return <div className="text-center text-lg font-semibold">No invoice data available</div>;

  const country = Country.getCountryByCode(bussiness?.country);
  const state = State.getStateByCodeAndCountry(bussiness?.state, bussiness?.country);
  const itemCountry = Country.getCountryByCode(item?.country);
  const itemState = State.getStateByCodeAndCountry(item?.state, item?.country);
  const isIntraState = bussiness?.state === item?.state;

  const calculateItemAmount = (item) => {
    const quantity = Number(item.quantity) || 0;
    const salePrice = Number(item.salePrice) || 0;
    const discountValue = parseFloat(item.discount || 0);
    const discountUnit = item.discountUnit || "%";
    const itemTotal = quantity * salePrice;
    let discountAmount = 0;
    if (discountUnit === "₹" || discountUnit === "INR" || discountUnit === "amount") {
      discountAmount = discountValue;
    } else {
      discountAmount = (itemTotal * discountValue) / 100;
    }
    return itemTotal - discountAmount;
  };

  const itemsToUse = item?.invoiceId?.startsWith("RT") ? item?.returnitems : item?.items;
  const totalQuantity = itemsToUse?.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0) || 0;
  const totalAmount = itemsToUse?.reduce((sum, i) => sum + (Number(i.quantity) * Number(i.salePrice) || 0), 0) || 0;
  const paidAmount = (parseFloat(item.transactionAmount) || 0) - (parseFloat(item.remainingTransactionAmount) || 0);
  const remainingBalance = item.remainingTransactionAmount || 0;

  const totalItemDiscount = itemsToUse?.reduce((sum, i) => {
    const discountValue = parseFloat(i.discount || 0);
    const discountUnit = i.discountUnit || "%";
    const quantity = parseFloat(i.quantity || 0);
    const salePrice = parseFloat(i.salePrice || 0);
    const itemTotal = quantity * salePrice;
    let discountAmount = 0;
    if (discountUnit === "₹" || discountUnit === "INR" || discountUnit === "amount") {
      discountAmount = discountValue;
    } else {
      discountAmount = (itemTotal * discountValue) / 100;
    }
    return sum + discountAmount;
  }, 0) || 0;

  const totalDiscount = totalItemDiscount;
  const isNonGSTInvoice = item?.showGST === false;
  const isReturnInvoice = item?.invoiceId?.startsWith("RT");
  const isEstimate = item?.saleType === "estimate";
  const isAdvancePayment = item?.saleType === "advancePayment";

  const hsnSummary = itemsToUse?.reduce((acc, item) => {
    const hsn = item.hsnNumber || item.hsn || "N/A";
    const taxableValue = Number(item.quantity) * Number(item.salePrice) || 0;
    const isNoGSTItem =
      isNonGSTInvoice ||
      (isIntraState && (!item.cgst || parseFloat(item.cgst) === 0) && (!item.sgst_utgst || parseFloat(item.sgst_utgst) === 0)) ||
      (!isIntraState && (!item.igst || parseFloat(item.igst) === 0));
    let totalGstRate = 0;
    let taxAmount = 0;
    if (!isNoGSTItem) {
      if (isIntraState) {
        totalGstRate = parseFloat(item.cgst || 0) + parseFloat(item.sgst_utgst || 0);
      } else {
        totalGstRate = parseFloat(item.igst || 0);
      }
      taxAmount = (taxableValue * totalGstRate) / 100;
    }
    if (!acc[hsn]) {
      acc[hsn] = {
        hsn: hsn,
        taxableValue: 0,
        cgstRate: isIntraState && !isNoGSTItem ? parseFloat(item.cgst || 0) : 0,
        sgstRate: isIntraState && !isNoGSTItem ? parseFloat(item.sgst_utgst || 0) : 0,
        igstRate: !isIntraState && !isNoGSTItem ? parseFloat(item.igst || 0) : 0,
        cessRate: parseFloat(item.cess || 0),
        cgstAmount: 0,
        sgstAmount: 0,
        igstAmount: 0,
        cessAmount: 0,
        totalTax: 0,
        totalGstRate: 0,
        isNoGST: isNoGSTItem,
      };
    }
    acc[hsn].taxableValue += taxableValue;
    acc[hsn].totalTax += taxAmount;
    acc[hsn].totalGstRate = totalGstRate;
    if (!isNoGSTItem) {
      if (isIntraState) {
        acc[hsn].cgstAmount += (taxableValue * parseFloat(item.cgst || 0)) / 100;
        acc[hsn].sgstAmount += (taxableValue * parseFloat(item.sgst_utgst || 0)) / 100;
      } else {
        acc[hsn].igstAmount += (taxableValue * parseFloat(item.igst || 0)) / 100;
      }
    }
    acc[hsn].cessAmount += (taxableValue * parseFloat(item.cess || 0)) / 100;
    return acc;
  }, {});

  const hsnSummaryArray = Object.values(hsnSummary || {});
  const totalTaxAmount = hsnSummaryArray.reduce((sum, hsn) => sum + hsn.totalTax + hsn.cessAmount, 0);
  const grandTotalBeforeDiscount = totalAmount + totalTaxAmount;
  const grandTotal = grandTotalBeforeDiscount - totalDiscount;
  const amountInwords = numberToWord(grandTotal);

  let currentAmount = grandTotal;
  const totalAdditionalDiscount = Array.isArray(item.additionalDiscount)
    ? item.additionalDiscount.reduce((sum, d) => {
        const amount = parseFloat(d.amount) || 0;
        let discountAmount = 0;
        if (d.type === "discount") {
          discountAmount = (currentAmount * amount) / 100;
        } else {
          discountAmount = amount;
        }
        currentAmount -= discountAmount;
        return sum + discountAmount;
      }, 0)
    : 0;

  const totalAdditionalFee = Array.isArray(item.additionalFee)
    ? item.additionalFee.reduce((sum, d) => {
        const amount = parseFloat(d.amount) || 0;
        let feeAmount = 0;
        if (d.type === "discount") {
          feeAmount = (currentAmount * amount) / 100;
        } else {
          feeAmount = amount;
        }
        currentAmount += feeAmount;
        return sum + feeAmount;
      }, 0)
    : 0;

  const isEntireInvoiceNoGST = isNonGSTInvoice || hsnSummaryArray.every((hsn) => hsn.isNoGST);

  return (
    <div
      ref={containerRef}
      className={`max-w-[210mm] mx-auto p-4 rounded-xl shadow-xl ${
        darkMode ? "bg-gray-800 text-gray-100" : "bg-white text-gray-900"
      }`}
      style={{ width: "210mm", boxSizing: "border-box" }}
    >
      {/* Header */}
      <div className={`flex justify-between items-center mb-4 p-4 rounded-lg ${selectedColor} bg-opacity-20`}>
        <div className="flex items-center space-x-4">
          <img
            src={bussiness?.businesslogo ? AWS_URL + bussiness.businesslogo : "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcT3dSEtDvpd9KAn4SdFJVoL0K1lj4Xfc-wVWQ&s"}
            alt="Business Logo"
            className="w-12 h-12 rounded-lg object-contain"
            //crossOrigin="anonymous"
          />
          <div>
            <h1 className="text-xl font-bold">{businessName || "Business Name"}</h1>
            {!isNonGSTInvoice && <p className="text-xs">GSTIN: {bussiness.gstNumber || "N/A"}</p>}
            <p className="text-xs">Email: {bussiness?.email || "N/A"}</p>
            <p className="text-xs">Address: {`${bussiness.firstLine}, ${state?.name}, ${country?.name}` || "N/A"}</p>
          </div>
        </div>
        <div className="text-right">
          <h2 className="text-lg font-semibold">
            {isReturnInvoice ? "Return Invoice/Debit Note" : isEstimate ? "Estimate Invoice" : "Tax Invoice"}
          </h2>
          {isNonGSTInvoice && !isEstimate && (
            <p className={`text-xs font-medium ${darkMode ? "text-red-300" : "text-red-500"}`}>
              Non-GST Invoice; cannot use for GST Returns
            </p>
          )}
          {isAdvancePayment && (
            <p className={`text-xs font-medium ${darkMode ? "text-blue-300" : "text-blue-500"}`}>
              Proforma Invoice
            </p>
          )}
        </div>
      </div>

      {/* Billing and Invoice Details */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="p-4 bg-opacity-10 bg-gray-100 rounded-lg">
          <h3 className="text-base font-semibold mb-2">{t("NEWINVOICE.BILLINGTO")}</h3>
          <p className="text-sm font-medium">{item.customerName || "-"}</p>
          <p className="text-xs">{t("NEWINVOICE.CONTACT_NO")}: {item.mobile || "-"}</p>
          <p className="text-xs">{`${item.address}, ${itemState?.name}, ${itemCountry?.name}` || "N/A"}</p>
          <p className={`text-xs font-medium ${darkMode ? "text-amber-300" : "text-amber-500"}`}>
            {isEntireInvoiceNoGST ? "No-GST Supply" : isIntraState ? "Intra-State Supply" : "Inter-State Supply"}
          </p>
        </div>
        <div className="p-4 bg-opacity-10 bg-gray-100 rounded-lg text-right">
          <p className="text-sm font-medium">{t("NEWINVOICE.NO")}: {item.invoiceId || "-"}</p>
          <p className="text-sm">{t("NEWINVOICE.DATE")}: {formatDate(item.saleDate)}</p>
          {item?.ewayBillDate && <p className="text-xs">Eway Bill Date: {item.ewayBillDate}</p>}
          {item?.ewayBillNo && <p className="text-xs">Eway Bill No: {item.ewayBillNo}</p>}
          {item?.validUpto && <p className="text-xs">Valid Upto: {item.validUpto}</p>}
          {item?.committedDateToPay && (
            <p className={`text-xs font-medium ${darkMode ? "text-red-300" : "text-red-500"}`}>
              {t("COMMON.PAYMENT_DUE_DATE")}: {formatDate(item.committedDateToPay)}
            </p>
          )}
        </div>
      </div>

      {/* Items Table */}
      <div className="mb-4">
        <div className="overflow-x-auto rounded-lg shadow">
          <table className="min-w-full border-collapse text-sm">
            <thead className={`${darkMode ? "bg-gray-700" : "bg-gray-100"} ${selectedColor} bg-opacity-30`}>
              <tr>
                <th className="py-2 px-3 text-left font-medium">Sl No.</th>
                <th className="py-2 px-3 text-left font-medium">Description</th>
                <th className="py-2 px-3 text-center font-medium">HSN/SAC</th>
                {!isEntireInvoiceNoGST && <th className="py-2 px-3 text-center font-medium">GST Rate</th>}
                <th className="py-2 px-3 text-center font-medium">Qty</th>
                <th className="py-2 px-3 text-center font-medium">Rate</th>
                <th className="py-2 px-3 text-center font-medium">Unit</th>
                <th className="py-2 px-3 text-center font-medium">Discount</th>
                <th className="py-2 px-3 text-right font-medium">Amount</th>
              </tr>
            </thead>
            <tbody>
              {itemsToUse?.map((itm, idx) => {
                const itemAmount = Number(itm.quantity) * Number(itm.salePrice) || 0;
                const discountRate = parseFloat(itm.discount || 0);
                const discountUnit = itm.discountUnit || "%";
                let discountAmount = 0;
                if (discountUnit === "₹" || discountUnit === "INR" || discountUnit === "amount") {
                  discountAmount = discountRate;
                } else {
                  discountAmount = (itemAmount * discountRate) / 100;
                }
                const isNoGSTItem =
                  isNonGSTInvoice ||
                  (isIntraState && (!itm.cgst || parseFloat(itm.cgst) === 0) && (!itm.sgst_utgst || parseFloat(itm.sgst_utgst) === 0)) ||
                  (!isIntraState && (!itm.igst || parseFloat(itm.igst) === 0));
                const gstRate = isIntraState
                  ? parseFloat(itm.cgst || 0) + parseFloat(itm.sgst_utgst || 0)
                  : parseFloat(itm.igst || 0);
                const gstAmount = !isNoGSTItem ? (itemAmount * gstRate) / 100 : 0;

                return (
                  <tr key={idx} className={`${idx % 2 === 0 ? (darkMode ? "bg-gray-700" : "bg-white") : (darkMode ? "bg-gray-600" : "bg-gray-50")}`}>
                    <td className="py-2 px-3 text-center">{idx + 1}</td>
                    <td className="py-2 px-3 font-medium">{itm.productName || itm.description || "-"}</td>
                    <td className="py-2 px-3 text-center">{truncateCommaSeparatedString(itm.hsnNumber) || itm.hsn || "N/A"}</td>
                    {!isEntireInvoiceNoGST && (
                      <td className="py-2 px-3 text-center">
                        {!isNoGSTItem ? (
                          <>
                            <div className="text-xs">({gstRate}%)</div>
                            <div>{formatCurrency(gstAmount, bussiness?.currency)}</div>
                            {parseFloat(itm.cess || 0) > 0 && (
                              <div className={`text-xs ${darkMode ? "text-amber-300" : "text-amber-500"}`}>
                                Cess: {itm.cess}% ({formatCurrency((itemAmount * parseFloat(itm.cess || 0)) / 100, bussiness?.currency)})
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="text-xs text-gray-400">No GST</div>
                        )}
                      </td>
                    )}
                    <td className="py-2 px-3 text-center">{itm.quantity || 0}</td>
                    <td className="py-2 px-3 text-right">{formatCurrency(itm.salePrice, bussiness?.currency)}</td>
                    <td className="py-2 px-3 text-center">{itm.unitSize || "Pcs"}</td>
                    <td className="py-2 px-3 text-center">
                      {discountRate > 0 ? (
                        <>
                          <div>{discountUnit === "₹" || discountUnit === "INR" || discountUnit === "amount" ? formatCurrency(discountRate, bussiness?.currency) : `${discountRate}%`}</div>
                          <div className={`text-xs ${darkMode ? "text-red-300" : "text-red-500"}`}>-{formatCurrency(discountAmount, bussiness?.currency)}</div>
                        </>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="py-2 px-3 text-right">{formatCurrency(itemAmount, bussiness?.currency)}</td>
                  </tr>
                );
              })}
              <tr className={`font-medium ${darkMode ? "bg-gray-700" : "bg-gray-100"}`}>
                <td colSpan={isEntireInvoiceNoGST ? 7 : 8} className="py-2 px-3 text-right">Total Amount</td>
                <td className="py-2 px-3 text-right">{formatCurrency(totalAmount, bussiness?.currency)}</td>
              </tr>
              {!isEntireInvoiceNoGST && (
                <>
                  {isIntraState && hsnSummaryArray.reduce((sum, hsn) => sum + hsn.cgstAmount, 0) > 0 && (
                    <tr className={`font-medium ${darkMode ? "bg-gray-700" : "bg-gray-100"}`}>
                      <td colSpan={8} className="py-2 px-3 text-right">Total CGST</td>
                      <td className="py-2 px-3 text-right">{formatCurrency(hsnSummaryArray.reduce((sum, hsn) => sum + hsn.cgstAmount, 0), bussiness?.currency)}</td>
                    </tr>
                  )}
                  {isIntraState && hsnSummaryArray.reduce((sum, hsn) => sum + hsn.sgstAmount, 0) > 0 && (
                    <tr className={`font-medium ${darkMode ? "bg-gray-700" : "bg-gray-100"}`}>
                      <td colSpan={8} className="py-2 px-3 text-right">Total SGST</td>
                      <td className="py-2 px-3 text-right">{formatCurrency(hsnSummaryArray.reduce((sum, hsn) => sum + hsn.sgstAmount, 0), bussiness?.currency)}</td>
                    </tr>
                  )}
                  {!isIntraState && hsnSummaryArray.reduce((sum, hsn) => sum + hsn.igstAmount, 0) > 0 && (
                    <tr className={`font-medium ${darkMode ? "bg-gray-700" : "bg-gray-100"}`}>
                      <td colSpan={8} className="py-2 px-3 text-right">Total IGST</td>
                      <td className="py-2 px-3 text-right">{formatCurrency(hsnSummaryArray.reduce((sum, hsn) => sum + hsn.igstAmount, 0), bussiness?.currency)}</td>
                    </tr>
                  )}
                  {hsnSummaryArray.reduce((sum, hsn) => sum + hsn.cessAmount, 0) > 0 && (
                    <tr className={`font-medium ${darkMode ? "bg-gray-700" : "bg-gray-100"}`}>
                      <td colSpan={8} className="py-2 px-3 text-right">Cess</td>
                      <td className="py-2 px-3 text-right">{formatCurrency(hsnSummaryArray.reduce((sum, hsn) => sum + hsn.cessAmount, 0), bussiness?.currency)}</td>
                    </tr>
                  )}
                </>
              )}
              <tr className={`font-medium ${darkMode ? "bg-gray-700" : "bg-gray-100"}`}>
                <td colSpan={isEntireInvoiceNoGST ? 7 : 8} className={`py-2 px-3 text-right ${darkMode ? "text-green-300" : "text-green-500"}`}>Amount before discount</td>
                <td className={`py-2 px-3 text-right ${darkMode ? "text-green-300" : "text-green-500"}`}>{formatCurrency(grandTotalBeforeDiscount, bussiness?.currency)}</td>
              </tr>
              {totalDiscount > 0 && (
                <tr className={`font-medium ${darkMode ? "bg-gray-700" : "bg-gray-100"}`}>
                  <td colSpan={isEntireInvoiceNoGST ? 7 : 8} className="py-2 px-3 text-right">Total Discount</td>
                  <td className={`py-2 px-3 text-right ${darkMode ? "text-red-300" : "text-red-500"}`}>-{formatCurrency(totalDiscount, bussiness?.currency)}</td>
                </tr>
              )}
              <tr className={`font-bold ${selectedColor} text-white`}>
                <td colSpan={isEntireInvoiceNoGST ? 7 : 8} className="py-2 px-3 text-right">Grand Total</td>
                <td className="py-2 px-3 text-right">{formatCurrency(grandTotal, bussiness?.currency)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* HSN Summary */}
      {!isEntireInvoiceNoGST && (
        <div className="mb-4">
          <h3 className="text-base font-semibold mb-2">HSN Wise Tax Summary</h3>
          <div className="overflow-x-auto rounded-lg shadow">
            <table className="min-w-full border-collapse text-sm">
              <thead className={`${darkMode ? "bg-gray-700" : "bg-gray-100"} ${selectedColor} bg-opacity-30`}>
                <tr>
                  <th className="py-2 px-3 text-left font-medium">HSN/SAC</th>
                  <th className="py-2 px-3 text-right font-medium">Taxable Value</th>
                  {isIntraState ? (
                    <>
                      <th className="py-2 px-3 text-center font-medium">CGST</th>
                      <th className="py-2 px-3 text-center font-medium">SGST/UTGST</th>
                    </>
                  ) : (
                    <th className="py-2 px-3 text-center font-medium">IGST</th>
                  )}
                  <th className="py-2 px-3 text-center font-medium">Cess</th>
                  <th className="py-2 px-3 text-center font-medium">Total Tax Amount</th>
                </tr>
              </thead>
              <tbody>
                {hsnSummaryArray.map((hsn, idx) => (
                  <tr key={idx} className={`${idx % 2 === 0 ? (darkMode ? "bg-gray-700" : "bg-white") : (darkMode ? "bg-gray-600" : "bg-gray-50")}`}>
                    <td className="py-2 px-3">{truncateCommaSeparatedString(hsn.hsn) || "N/A"}</td>
                    <td className="py-2 px-3 text-right">{formatCurrency(hsn.taxableValue, bussiness?.currency)}</td>
                    {isIntraState ? (
                      <>
                        <td className="py-2 px-3 text-center">
                          {hsn.isNoGST ? (
                            <div className="text-gray-400">N/A</div>
                          ) : (
                            <>
                              <div>{hsn.cgstRate}%</div>
                              <div>{formatCurrency(hsn.cgstAmount, bussiness?.currency)}</div>
                            </>
                          )}
                        </td>
                        <td className="py-2 px-3 text-center">
                          {hsn.isNoGST ? (
                            <div className="text-gray-400">N/A</div>
                          ) : (
                            <>
                              <div>{hsn.sgstRate}%</div>
                              <div>{formatCurrency(hsn.sgstAmount, bussiness?.currency)}</div>
                            </>
                          )}
                        </td>
                      </>
                    ) : (
                      <td className="py-2 px-3 text-center">
                        {hsn.isNoGST ? (
                          <div className="text-gray-400">N/A</div>
                        ) : (
                          <>
                            <div>{hsn.igstRate}%</div>
                            <div>{formatCurrency(hsn.igstAmount, bussiness?.currency)}</div>
                          </>
                        )}
                      </td>
                    )}
                    <td className="py-2 px-3 text-center">
                      <div>{hsn.cessRate}%</div>
                      <div>{formatCurrency(hsn.cessAmount, bussiness?.currency)}</div>
                    </td>
                    <td className="py-2 px-3 text-center">
                      <div>{hsn.isNoGST ? hsn.cessRate : hsn.totalGstRate + hsn.cessRate}%</div>
                      <div>{formatCurrency(hsn.totalTax + hsn.cessAmount, bussiness?.currency)}</div>
                    </td>
                  </tr>
                ))}
                <tr className={`font-bold ${selectedColor} text-white`}>
                  <td className="py-2 px-3">Total</td>
                  <td className="py-2 px-3 text-right">{formatCurrency(totalAmount, bussiness?.currency)}</td>
                  {isIntraState ? (
                    <>
                      <td className="py-2 px-3 text-center">{formatCurrency(hsnSummaryArray.reduce((sum, hsn) => sum + hsn.cgstAmount, 0), bussiness?.currency)}</td>
                      <td className="py-2 px-3 text-center">{formatCurrency(hsnSummaryArray.reduce((sum, hsn) => sum + hsn.sgstAmount, 0), bussiness?.currency)}</td>
                    </>
                  ) : (
                    <td className="py-2 px-3 text-center">{formatCurrency(hsnSummaryArray.reduce((sum, hsn) => sum + hsn.igstAmount, 0), bussiness?.currency)}</td>
                  )}
                  <td className="py-2 px-3 text-center">{formatCurrency(hsnSummaryArray.reduce((sum, hsn) => sum + hsn.cessAmount, 0), bussiness?.currency)}</td>
                  <td className="py-2 px-3 text-center">{formatCurrency(totalTaxAmount, bussiness?.currency)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Summary and Terms */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="p-4 bg-opacity-10 bg-gray-100 rounded-lg">
          <div className="mb-3">
            <h3 className="text-sm font-semibold">Amount Chargeable (in words)</h3>
            <p className={`text-xs italic ${darkMode ? "text-gray-300" : "text-gray-600"}`}>{numberToWord(grandTotal)} Rupees only</p>
          </div>
          {!isEntireInvoiceNoGST && (
            <div className="mb-3">
              <h3 className="text-sm font-semibold">Tax Amount (in words)</h3>
              <p className={`text-xs italic ${darkMode ? "text-gray-300" : "text-gray-600"}`}>{numberToWord(totalTaxAmount)} Rupees only</p>
            </div>
          )}
          <div className="pt-3 border-t border-gray-300">
            <h3 className="text-sm font-semibold mb-2">Terms & Declaration</h3>
            <div className={`text-xs ${darkMode ? "text-gray-300" : "text-gray-600"} space-y-2`}>
              <p><strong>Declaration:</strong></p>
              <ul className="list-disc list-inside space-y-1">
                <li>We declare that this invoice shows the actual price of the goods described</li>
                <li>All particulars shown are true and correct to the best of our knowledge</li>
              </ul>
              <p><strong>Terms:</strong></p>
              <ul className="list-disc list-inside space-y-1">
                <li>Goods once sold will not be taken back</li>
                <li>Payment due within 30 days from invoice date</li>
              </ul>
            </div>
          </div>
        </div>
        <div>
          <div className="p-4 bg-opacity-10 bg-gray-100 rounded-lg max-w-xs ml-auto">
            <table className="w-full text-sm">
              <tbody>
                <tr className="font-medium">
                  <td className="py-1 px-2">{t("NEWINVOICE.SUB_TOTAL")}</td>
                  <td className="py-1 px-2 text-right">{formatCurrency(grandTotal, bussiness?.currency)}</td>
                </tr>
                {totalAdditionalDiscount > 0 && (
                  <tr>
                    <td className="py-1 px-2">
                      Additional Discounts
                      {item.additionalDiscount?.map((discount, idx) => (
                        <div key={idx} className="text-xs">
                          • {discount.label}: {discount.amount} {discount.type === "discount" ? "%" : "₹"}
                        </div>
                      ))}
                    </td>
                    <td className="py-1 px-2 text-right">-{formatCurrency(totalAdditionalDiscount, bussiness?.currency)}</td>
                  </tr>
                )}
                {totalAdditionalFee > 0 && (
                  <tr>
                    <td className="py-1 px-2">
                      Additional Fees
                      {item.additionalFee?.map((fee, idx) => (
                        <div key={idx} className="text-xs">
                          • {fee.label}: {fee.amount} {fee.type === "discount" ? "%" : "₹"}
                        </div>
                      ))}
                    </td>
                    <td className="py-1 px-2 text-right">+{formatCurrency(totalAdditionalFee, bussiness?.currency)}</td>
                  </tr>
                )}
                {item.subscriptionDisc > 0 && (
                  <tr>
                    <td className="py-1 px-2">{t("INVOICE.SUBSCRIPTION_SAVINGS")}</td>
                    <td className="py-1 px-2 text-right">-{formatCurrency(item.subscriptionDisc, bussiness?.currency)}</td>
                  </tr>
                )}
                <tr className={`font-bold ${selectedColor} text-white`}>
                  <td className="py-1 px-2">{t("COMMON.TOTAL_AMOUNT")}</td>
                  <td className="py-1 px-2 text-right">{formatCurrency(grandTotal - totalAdditionalDiscount + totalAdditionalFee - (item.subscriptionDisc || 0), bussiness?.currency)}</td>
                </tr>
                {(isAdvancePayment || item?.saleType === "advanceSale") && (
                  <tr>
                    <td className="py-1 px-2">Advance Amount</td>
                    <td className="py-1 px-2 text-right">{formatCurrency(item.advancePayment, bussiness?.currency)}</td>
                  </tr>
                )}
                <tr>
                  <td className="py-1 px-2">{t("NEWINVOICE.PAYMENT_MODE")}</td>
                  <td className="py-1 px-2 text-right">{item.paymentMode || "-"}</td>
                </tr>
                <tr>
                  <td className="py-1 px-2">{isAdvancePayment ? "Remaining Balance" : t("COMMON.TOTAL_VALUE")}</td>
                  <td className="py-1 px-2 text-right">
                    {isReturnInvoice
                      ? formatCurrency(item.totalReturnAmount, bussiness?.currency)
                      : formatCurrency(grandTotal - totalAdditionalDiscount + totalAdditionalFee - (item.subscriptionDisc || 0) - parseFloat(item.advancePayment || 0), bussiness?.currency)}
                  </td>
                </tr>
                {item.remainingTransactionAmount > 0 && (
                  <>
                    <tr>
                      <td className="py-1 px-2">{t("NEWINVOICE.RECEIVED")}</td>
                      <td className="py-1 px-2 text-right">{formatCurrency(paidAmount, bussiness?.currency)}</td>
                    </tr>
                    <tr className="font-bold">
                      <td className="py-1 px-2">Due Balance</td>
                      <td className="py-1 px-2 text-right">{formatCurrency(remainingBalance, bussiness?.currency)}</td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
          <div className="mt-4 p-4 bg-opacity-10 bg-gray-100 rounded-lg text-center">
            {bussiness?.signature ? (
              <img src={AWS_URL + bussiness.signature} alt="Signature" className="w-24 h-16 object-contain mx-auto"  />
            ) : (
              <span className="text-gray-400 text-xs">Signature</span>
            )}
            <p className="mt-2 text-sm font-medium">{t("NEWINVOICE.AUTHORIZED_SIGNATORY")}</p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className={`p-4 rounded-lg ${selectedColor} bg-opacity-20`}>
        <div className="flex justify-between items-center">
          <div>
            <div className="mb-3">
              <h3 className="text-sm font-semibold">{t("NEWINVOICE.AMOUNT_IN_WORDS")}</h3>
              <p className={`text-xs italic ${darkMode ? "text-gray-300" : "text-gray-600"}`}>{amountInwords} Rupees only</p>
            </div>
            {item.notes && (
              <div className="mb-3">
                <h3 className="text-sm font-semibold">Notes</h3>
                <p className={darkMode ? "text-gray-300" : "text-gray-600"}>{item.notes}</p>
              </div>
            )}
            <p className={darkMode ? "text-gray-300" : "text-gray-600"}>{t("NEWINVOICE.FOR_COMPANY")}: {bussiness.businessName}</p>
            <p className={darkMode ? "text-gray-300" : "text-gray-600"}>{item.authorizedPerson || ""}</p>
          </div>
          {bussiness?.aadharPhoto && (
            <img src={AWS_URL + bussiness?.aadharPhoto} alt="Payment QR Code" className="w-20 h-20 object-contain"  />
          )}
        </div>
      </div>
    </div>
  );
};

export default ModernInvoice2;


